import { Avatar, Box, Button, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import GroupsRounded from '@mui/icons-material/GroupsRounded';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import { useMutation } from '@apollo/client';
import { useSnackbar } from 'notistack';
import posthog from 'posthog-js';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { LogoutDocument } from '@/graphql/generated/logout.generated';
import { betterAuthClient } from '@/libs/better-auth-client';
import { authApi, isAuthApiError } from './auth-api';
import { mapAuthError } from './auth-error-map';
import {
    clearLastUsedEmail,
    decodeInviteHint,
    getAuthLoginUrl,
    getAuthRegisterUrl,
    getCurrentRelativeUrl,
    getRedirectPath,
    redirectAfterAuth,
} from './auth-utils';
import AuthSplitLayout from './AuthSplitLayout';

// Subtle, reduced-motion-safe entrance for the card content.
const REVEAL_SX = {
    '@keyframes inviteRise': {
        from: { opacity: 0, transform: 'translateY(8px)' },
        to: { opacity: 1, transform: 'none' },
    },
    animation: 'inviteRise 280ms cubic-bezier(0.22, 1, 0.36, 1) both',
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
} as const;

/** Organization monogram + name — gives the invitation a sender identity. */
function OrgIdentity({ name }: { name: string }) {
    const { t } = useTranslation();
    const initial = name.trim().charAt(0).toUpperCase();

    return (
        <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
                variant="rounded"
                sx={{
                    width: 46,
                    height: 46,
                    fontWeight: 700,
                    fontSize: '1.15rem',
                    color: 'primary.main',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.14),
                }}
            >
                {initial || <GroupsRounded />}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700} noWrap>
                    {name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {t('auth.invite.session.invited-caption')}
                </Typography>
            </Box>
        </Stack>
    );
}

/** The invited email shown as a scannable pill — the account the invite must be accepted with. */
function AcceptingAsPill({ email, label }: { email: string; label?: string }) {
    const { t } = useTranslation();

    return (
        <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                {label ?? t('auth.invite.accepting-as')}
            </Typography>
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <MailOutlineRounded fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
                <Typography sx={{ fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>
                    {email}
                </Typography>
            </Stack>
        </Box>
    );
}

type InviteState =
    | { type: 'loading' }
    | { type: 'need-login'; email?: string; organizationName?: string }
    | {
          type: 'ready';
          invitationId: string;
          organizationName: string;
          organizationSlug: string;
          email: string;
      }
    | {
          type: 'wrong-account';
          signedInEmail?: string;
          invitedEmail?: string;
          organizationName?: string;
      }
    | { type: 'done' }
    | { type: 'rejected' }
    | { type: 'error'; message: string };

export default function AuthAcceptInvitation() {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const redirectPath = useMemo(() => getRedirectPath(), []);
    const invitationId = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('invitationId') || '';
    }, []);
    // The invite email carries a signed `hint` (JWT) with the invited email + org name so we can
    // pre-fill them before the user has a session. Signature is validated server-side on accept.
    const inviteHint = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return decodeInviteHint(params.get('hint'));
    }, []);

    const [state, setState] = useState<InviteState>({ type: 'loading' });
    const [submitting, setSubmitting] = useState(false);
    const [logout] = useMutation(LogoutDocument);

    useEffect(() => {
        if (!invitationId) {
            setState({ type: 'error', message: t('auth.invite.invalid') });
            return;
        }

        let signedInEmail: string | undefined;
        const load = async () => {
            try {
                const session = await authApi.getSession();
                signedInEmail = (session?.user as { email?: string } | undefined)?.email;
                if (!session?.session) {
                    setState({
                        type: 'need-login',
                        email: inviteHint?.email,
                        organizationName: inviteHint?.organizationName,
                    });
                    return;
                }

                const invitation = await authApi.getInvitation(invitationId);
                setState({
                    type: 'ready',
                    invitationId,
                    organizationName: invitation.organizationName,
                    organizationSlug: invitation.organizationSlug,
                    email: invitation.email,
                });
            } catch (error) {
                // Signed in with another account (often the inviter's own, on the same browser).
                // This is not an expired link: offer to switch accounts instead.
                if (
                    isAuthApiError(error) &&
                    error.code?.endsWith('YOU_ARE_NOT_THE_RECIPIENT_OF_THE_INVITATION')
                ) {
                    setState({
                        type: 'wrong-account',
                        signedInEmail,
                        invitedEmail: inviteHint?.email,
                        organizationName: inviteHint?.organizationName,
                    });
                    return;
                }

                const mapped = mapAuthError(error, t, 'invitation');
                setState({
                    type: 'error',
                    message: mapped.message,
                });
            }
        };

        void load();
    }, [invitationId, inviteHint, t]);

    const onSwitchAccount = async () => {
        if (state.type !== 'wrong-account') {
            return;
        }

        setSubmitting(true);
        // The GraphQL logout ends both the Better Auth session and the legacy session cookie. Signing
        // out of Better Auth alone would leave the old account's legacy cookie behind.
        await logout().catch(() => betterAuthClient.signOut().catch(() => undefined));
        posthog.reset();
        clearLastUsedEmail();
        // Back to this page after login, with the invited email locked when we know it.
        const linkOpts = state.invitedEmail ? { email: state.invitedEmail, lockEmail: true } : undefined;
        window.location.href = getAuthLoginUrl(getCurrentRelativeUrl(), linkOpts);
    };

    const onAccept = async () => {
        if (state.type !== 'ready') {
            return;
        }

        setSubmitting(true);

        try {
            await authApi.acceptInvitation(state.invitationId);
            // Land the user inside the workspace they just joined by making it their active
            // organization before the redirect. Best-effort: if it fails, the reload still logs
            // them into their previous workspace (authorize falls back gracefully on membership).
            await authApi
                .setActiveOrganization({ organizationSlug: state.organizationSlug })
                .catch(() => undefined);
            setState({ type: 'done' });
            enqueueSnackbar(t('auth.invite.accepted'));
            // Full reload (into the invite's callback path) so the backend re-resolves the now-active
            // workspace and re-mints the legacy session token for it — landing the user inside the
            // workspace they just joined.
            redirectAfterAuth(redirectPath);
        } catch (error) {
            const mapped = mapAuthError(error, t, 'invitation');
            enqueueSnackbar(mapped.message, { variant: 'error' });
            setSubmitting(false);
        }
    };

    const onReject = async () => {
        if (state.type !== 'ready') {
            return;
        }

        setSubmitting(true);

        try {
            await authApi.rejectInvitation(state.invitationId);
            setState({ type: 'rejected' });
            enqueueSnackbar(t('auth.invite.rejected'));
        } catch (error) {
            const mapped = mapAuthError(error, t, 'invitation');
            enqueueSnackbar(mapped.message, { variant: 'error' });
            setSubmitting(false);
        }
    };

    if (state.type === 'loading') {
        return (
            <AuthSplitLayout>
                <Stack alignItems="center" justifyContent="center" minHeight={200}>
                    <Typography>{t('auth.common.loading')}</Typography>
                </Stack>
            </AuthSplitLayout>
        );
    }

    if (state.type === 'need-login') {
        const returnPath = getCurrentRelativeUrl();
        // Carry the invited email into register/login pre-filled + locked, so the account they create
        // or sign into matches the invitation (Better Auth enforces the email match on accept).
        const linkOpts = state.email ? { email: state.email, lockEmail: true } : undefined;

        return (
            <AuthSplitLayout
                footer={
                    <Typography variant="body2" color="text.secondary">
                        {t('auth.register.have-account')}{' '}
                        <Typography
                            component="a"
                            href={getAuthLoginUrl(returnPath, linkOpts)}
                            color="primary.main"
                            sx={{ textDecoration: 'none', fontWeight: 600 }}
                        >
                            {t('auth.register.go-login')}
                        </Typography>
                    </Typography>
                }
            >
                <Stack spacing={2.5} sx={REVEAL_SX}>
                    {state.organizationName && <OrgIdentity name={state.organizationName} />}

                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            {t('auth.invite.session.title')}
                        </Typography>
                        <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                            {state.email
                                ? t('auth.invite.session.description')
                                : t('auth.invite.session.description-generic')}
                        </Typography>
                    </Box>

                    {state.email && <AcceptingAsPill email={state.email} />}

                    <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        href={getAuthRegisterUrl(returnPath, linkOpts)}
                        sx={{ mt: 0.5, py: 1.25 }}
                    >
                        {t('auth.invite.create-account')}
                    </Button>
                </Stack>
            </AuthSplitLayout>
        );
    }

    if (state.type === 'wrong-account') {
        return (
            <AuthSplitLayout>
                <Stack spacing={2.5} sx={REVEAL_SX}>
                    {state.organizationName && <OrgIdentity name={state.organizationName} />}

                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            {t('auth.invite.wrong-account.title')}
                        </Typography>
                        <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                            {t('auth.invite.wrong-account.description')}
                        </Typography>
                    </Box>

                    {state.signedInEmail && (
                        <AcceptingAsPill
                            email={state.signedInEmail}
                            label={t('auth.invite.wrong-account.signed-in-as')}
                        />
                    )}
                    {state.invitedEmail && (
                        <AcceptingAsPill
                            email={state.invitedEmail}
                            label={t('auth.invite.wrong-account.invited-email')}
                        />
                    )}

                    <Stack spacing={1} sx={{ mt: 0.5 }}>
                        <Button
                            variant="contained"
                            size="large"
                            fullWidth
                            disabled={submitting}
                            onClick={onSwitchAccount}
                            data-id="invite-wrong-account-switch"
                            sx={{ py: 1.25 }}
                        >
                            {submitting ? t('auth.common.loading') : t('auth.invite.wrong-account.switch')}
                        </Button>
                        <Button
                            component={Link}
                            to="/"
                            variant="text"
                            size="large"
                            fullWidth
                            disabled={submitting}
                        >
                            {t('error.page.link-expired.action.go-to-swonkie')}
                        </Button>
                    </Stack>
                </Stack>
            </AuthSplitLayout>
        );
    }

    if (state.type === 'error') {
        return (
            <AuthSplitLayout>
                <Stack spacing={2}>
                    <Typography variant="h5" fontWeight={700}>
                        {t('error.page.link-expired.title')}
                    </Typography>
                    <Typography color="text.secondary">{state.message}</Typography>
                    <Button component={Link} to="/" variant="outlined" size="large" fullWidth>
                        {t('error.page.link-expired.action.go-to-swonkie')}
                    </Button>
                </Stack>
            </AuthSplitLayout>
        );
    }

    if (state.type === 'done') {
        return (
            <AuthSplitLayout>
                <Stack spacing={2}>
                    <Typography variant="h5" fontWeight={700}>
                        {t('auth.invite.accepted-title')}
                    </Typography>
                    <Typography color="text.secondary">{t('auth.invite.accepted')}</Typography>
                </Stack>
            </AuthSplitLayout>
        );
    }

    if (state.type === 'rejected') {
        return (
            <AuthSplitLayout>
                <Stack spacing={2}>
                    <Typography variant="h5" fontWeight={700}>
                        {t('auth.invite.rejected-title')}
                    </Typography>
                    <Typography color="text.secondary">{t('auth.invite.rejected')}</Typography>
                    <Button component={Link} to="/" variant="outlined" size="large" fullWidth>
                        {t('error.page.link-expired.action.go-to-swonkie')}
                    </Button>
                </Stack>
            </AuthSplitLayout>
        );
    }

    return (
        <AuthSplitLayout>
            <Stack spacing={2.5} sx={REVEAL_SX}>
                <OrgIdentity name={state.organizationName} />

                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        {t('auth.invite.session.title')}
                    </Typography>
                </Box>

                <AcceptingAsPill email={state.email} />

                <Stack spacing={1} sx={{ mt: 0.5 }}>
                    <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        disabled={submitting}
                        onClick={onAccept}
                        sx={{ py: 1.25 }}
                    >
                        {submitting ? t('auth.common.loading') : t('auth.invite.accept')}
                    </Button>
                    <Button
                        variant="text"
                        size="large"
                        fullWidth
                        disabled={submitting}
                        onClick={onReject}
                        sx={{ color: 'text.secondary' }}
                    >
                        {t('auth.invite.reject')}
                    </Button>
                </Stack>
            </Stack>
        </AuthSplitLayout>
    );
}
